import type { PreviewType } from '#lib/route-utils.js';
import type { ToolResultGroup, ToolResultItem } from './types.js';

import { formatTimeInOriginalTimezone } from '#lib/date-utils.js';

/** Shared low-level reader helpers used by tool specs to pluck values from arbitrary tool outputs. */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

export function readArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

export function readField(obj: unknown, key: string): unknown {
  return isRecord(obj) ? obj[key] : undefined;
}

/** Build a list extractor for the common `{ [arrayKey]: Item[] }` shape. */
export function makeListExtractor(options: {
  arrayKey: string;
  previewType: PreviewType;
  idFields: readonly string[];
  titleFields: readonly string[];
  subtitleFields?: readonly string[];
}): (output: unknown) => ToolResultGroup | undefined {
  const { arrayKey, previewType, idFields, titleFields, subtitleFields = [] } = options;
  return (output) => {
    const arr = readArray(readField(output, arrayKey));
    if (!arr) return undefined;
    const items: ToolResultItem[] = [];
    for (const [index, entry] of arr.entries()) {
      const id = idFields.map((f) => readString(readField(entry, f))).find((v) => v != null);
      if (!id) continue;
      const title =
        titleFields.map((f) => readString(readField(entry, f))).find((v) => v != null) ?? `Result ${index + 1}`;
      const subtitle = subtitleFields.map((f) => readString(readField(entry, f))).find((v) => v != null);
      items.push({ key: `${id}-${index}`, title, subtitle, previewType, id });
    }
    return { items };
  };
}

/** Collapse whitespace and cap long strings so they fit in a single-line badge label. */
export function truncateForLabel(value: string, maxLength = 60): string {
  const collapsed = value.replaceAll(/\s+/g, ' ').trim();
  if (collapsed.length <= maxLength) return collapsed;
  return `${collapsed.slice(0, maxLength - 1).trimEnd()}…`;
}

/** Build a "for \"query\"" suffix used as the contextLabel on expandable result badges. */
export function queryContextLabel(query: string | undefined): string | undefined {
  if (!query) return undefined;
  return `for "${truncateForLabel(query)}"`;
}

/** Build a `<verb> "<query>"` single-line label for active / completed search badges. */
export function withQueryLabel(baseLabel: string, query: string | undefined): string {
  if (!query) return baseLabel;
  return `${baseLabel} "${truncateForLabel(query)}"`;
}

/** Resolve the `/favicon.ico` URL for a given origin. Returns undefined for malformed URLs. */
export function faviconUrl(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname) return undefined;
    // Pull the favicon from the origin itself. When the site has no favicon the origin
    // returns a real 404 (or non-image HTML), so the <img> onError handler can swap to
    // a Material fallback. Favicon aggregator services (DDG / Google) serve a synthetic
    // placeholder image instead, which never fires onError.
    return `${parsed.protocol}//${parsed.hostname}/favicon.ico`;
  } catch {
    return undefined;
  }
}

/** Format an ISO timestamp as "h:mm a" in its original timezone, matching FlightCard. */
export function formatClockTime(value: unknown): string | undefined {
  const raw = readString(value);
  if (!raw) return undefined;
  const formatted = formatTimeInOriginalTimezone(raw, 'h:mm a');
  return formatted || undefined;
}

/* -------------------------- Flight extractor helpers -------------------------- */

function findFlightResultsArray(output: unknown): unknown[] | undefined {
  const candidates: unknown[] = [
    readField(output, 'results'),
    readField(readField(output, 'outboundResults'), 'results'),
    readField(readField(output, 'outbound'), 'results'),
    readField(readField(readField(output, 'suspendPayload'), 'awaitFlightSelection'), 'results'),
    readField(output, 'flights'),
  ];
  for (const c of candidates) {
    const arr = readArray(c);
    if (arr && arr.length > 0) return arr;
  }
  return undefined;
}

export type FlightLeg = 'outbound' | 'return';

/** Build a flight extractor that reads the matching segment array (outbound or return). */
export function makeFlightExtractor(leg: FlightLeg): (output: unknown) => ToolResultGroup | undefined {
  return (output) => {
    const arr = findFlightResultsArray(output);
    if (!arr) return undefined;
    const segmentsKey = leg === 'outbound' ? 'outboundSegments' : 'returnSegments';
    const fallbackKey = leg === 'outbound' ? 'returnSegments' : 'outboundSegments';
    const items: ToolResultItem[] = [];
    let firstOrigin: string | undefined;
    let firstDestination: string | undefined;
    for (const [index, entry] of arr.entries()) {
      const id = readString(readField(entry, 'id'));
      if (!id) continue;
      const primary = readArray(readField(entry, segmentsKey)) ?? [];
      const segments = primary.length > 0 ? primary : (readArray(readField(entry, fallbackKey)) ?? []);
      const first = segments[0];
      const last = segments.at(-1);
      const origin =
        readString(readField(readField(first, 'origin'), 'iata_code')) ??
        readString(readField(first, 'origin')) ??
        readString(readField(first, 'departureAirport'));
      const destination =
        readString(readField(readField(last, 'destination'), 'iata_code')) ??
        readString(readField(last, 'destination')) ??
        readString(readField(last, 'arrivalAirport'));
      firstOrigin ??= origin;
      firstDestination ??= destination;
      const departureTime = formatClockTime(readField(first, 'departure'));
      const arrivalTime = formatClockTime(readField(last, 'arrival'));
      const airlineLogo = readString(readField(readField(first, 'airline'), 'logo_url'));
      const travelClass = readString(readField(first, 'travelClass'));
      const stopCount = Math.max(0, segments.length - 1);
      const stopsLabel = stopCount === 0 ? 'Direct' : stopCount === 1 ? '1 stop' : `${stopCount} stops`;
      const subtitle = [stopsLabel, travelClass].filter(Boolean).join(' · ');
      const leftSide = [origin, departureTime].filter(Boolean).join(' ');
      const rightSide = [destination, arrivalTime].filter(Boolean).join(' ');
      const routeStr = leftSide && rightSide ? `${leftSide} → ${rightSide}` : `Flight ${index + 1}`;
      items.push({
        key: `${id}-${index}`,
        title: routeStr,
        subtitle: subtitle || undefined,
        previewType: 'flight',
        id,
        iconUrl: airlineLogo,
      });
    }
    const contextLabel =
      firstOrigin && firstDestination ? `from ${firstOrigin} to ${firstDestination}` : undefined;
    return { items, contextLabel };
  };
}

/* -------------------------- Single-URL extractor (web scrape / url fetch) -------------------------- */

export function extractSingleUrl(output: unknown): ToolResultGroup | undefined {
  const url = readString(readField(output, 'url'));
  if (!url) return undefined;
  const title = readString(readField(output, 'title')) ?? url;
  const summary = readString(readField(output, 'summary')) ?? readString(readField(output, 'description'));
  return {
    items: [
      {
        key: url,
        title,
        subtitle: summary ?? url,
        href: url,
        iconUrl: faviconUrl(url),
        iconVariant: 'logo',
      },
    ],
  };
}
